import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { PageHeader } from "@/components/Heading";
import { formatIndianCurrency, formatTime } from "@/lib/format";
import { CreditCard, Smartphone, Wallet, IndianRupee } from "lucide-react";
import { useState, useMemo } from "react";

const PAY_ICON = { Cash: IndianRupee, UPI: Smartphone, Card: CreditCard, Wallet: Wallet };

export default function Pulse() {
  const { data } = useSWR("/pulse/feed?limit=80", fetcher, { refreshInterval: 30000 });
  const [storeFilter, setStoreFilter] = useState("ALL");
  const [payFilter, setPayFilter] = useState("ALL");

  const txns = data?.transactions || [];
  const stores = useMemo(() => Array.from(new Set(txns.map((t) => t.store))).sort(), [txns]);
  const filtered = txns.filter((t) =>
    (storeFilter === "ALL" || t.store === storeFilter) &&
    (payFilter === "ALL" || t.payment_mode === payFilter)
  );

  return (
    <div>
      <PageHeader
        eyebrow="Live · refreshes every 30s"
        title="Pulse Feed"
        lede="Every voucher, as it rings. New rows slide in from the top."
        right={
          <div className="flex gap-2">
            <div className="hairline px-3 h-9 grid place-items-center font-mono text-xs">
              <span className="inline-block w-2 h-2 bg-primary mr-2 animate-pulse" />
              LIVE
            </div>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="pulse-filters">
        <select
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
          className="hairline h-9 px-2 bg-background font-mono text-xs"
        >
          <option value="ALL">All Stores</option>
          {stores.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select
          value={payFilter}
          onChange={(e) => setPayFilter(e.target.value)}
          className="hairline h-9 px-2 bg-background font-mono text-xs"
        >
          <option value="ALL">All Modes</option>
          <option>Cash</option><option>UPI</option><option>Card</option><option>Wallet</option>
        </select>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block hairline">
        <table className="w-full table-dense">
          <thead>
            <tr>
              <th className="w-16">Time</th>
              <th>Voucher</th>
              <th>Store</th>
              <th>Cashier</th>
              <th>Customer</th>
              <th className="text-right">Amount</th>
              <th>Mode</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => {
              const PayIcon = PAY_ICON[t.payment_mode] || IndianRupee;
              return (
                <tr key={t.voucher_no} className="row-enter" data-testid="pulse-feed-row">
                  <td className="text-muted-foreground">{formatTime(t.voucher_time)}</td>
                  <td className="text-[11px]">{t.voucher_no}</td>
                  <td>{t.store}</td>
                  <td className="text-muted-foreground">{t.cashier}</td>
                  <td>
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest hairline ${
                      t.customer_type === "new" ? "text-primary border-primary" : ""
                    }`}>
                      {t.customer_type}
                    </span>
                  </td>
                  <td className="text-right font-mono">{formatIndianCurrency(t.amount)}</td>
                  <td>
                    <span className="inline-flex items-center gap-1.5 text-[11px]">
                      <PayIcon className="w-3 h-3" /> {t.payment_mode}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {filtered.map((t) => (
          <div key={t.voucher_no} className="hairline p-3 row-enter" data-testid="pulse-feed-row-mobile">
            <div className="flex justify-between text-xs">
              <span className="font-mono text-muted-foreground">{formatTime(t.voucher_time)}</span>
              <span className="font-mono">{formatIndianCurrency(t.amount)}</span>
            </div>
            <div className="font-display text-sm mt-1">{t.store}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{t.cashier} · {t.payment_mode}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
